import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowDownstreamMetricsFilteredByFlowOutputFieldQuery,
  FlowDownstreamMetricsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamMetricsConnection_nodes,
  FlowDownstreamMetricsFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamMetricsFilteredByFlowOutputFieldQuery';
import {
  FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery,
  // tslint:disable-next-line:max-line-length
  FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamMetricsConnection_nodes,
  FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery';
import {
  FlowDownstreamMetricsQuery,
  FlowDownstreamMetricsQuery_flows_downstreamMetricsConnection_nodes,
  FlowDownstreamMetricsQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamMetricsQuery';
import {
  FlowDownstreamMetricsWithoutPersonalSpaceQuery,
  FlowDownstreamMetricsWithoutPersonalSpaceQuery_flows_downstreamMetricsConnection_nodes,
  FlowDownstreamMetricsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamMetricsWithoutPersonalSpaceQuery';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import { FlowDownstreamMetricsFilteredByFlowOutputFieldQueryGql } from '../../queries/FlowDownstreamMetricsFilteredByFlowOutputFieldQueryGql';
import { FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql';
import { FlowDownstreamMetricsQueryGql } from '../../queries/FlowDownstreamMetricsQueryGql';
import { FlowDownstreamMetricsWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamMetricsWithoutPersonalSpaceQueryGql';
import { getRowDataForMetric, getRowDataForMetricWithoutPersonalSpace, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = FlowDownstreamMetricsQueryVariables;
type QueryResults = FlowDownstreamMetricsQuery;
type QueryResultListNode = FlowDownstreamMetricsQuery_flows_downstreamMetricsConnection_nodes;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = FlowDownstreamMetricsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = FlowDownstreamMetricsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace =
  FlowDownstreamMetricsWithoutPersonalSpaceQuery_flows_downstreamMetricsConnection_nodes;

type QueryVariablesWithFilter = FlowDownstreamMetricsFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamMetricsFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowDownstreamMetricsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamMetricsConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace =
  FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery;
// tslint:disable-next-line:max-line-length
type QueryResultListNodeWithFilterWithoutPersonalSpace = FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamMetricsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamMetricsConnection : null;
}

function selectConnectionListResultWithoutPersonalSpace(
  resultData: QueryResultsWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamMetricsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamMetricsConnection : null;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamMetricsConnection : null;
}

export type FlowDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamMetricsQueryGrid(props: FlowDownstreamMetricsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFlowOutputFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamMetricsFilteredByFlowOutputFieldQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as MetricSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForMetric}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        // Query without Selection
        <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
          queryGql={FlowDownstreamMetricsQueryGql}
          partialQueryVariables={{
            flowVizportalUrlId: props.flowVizportalUrlId,
            orderBy: props.orderBy as MetricSortOrder,
          }}
          selectConnectionListResult={selectConnectionListResult}
          mapResultNode={getRowDataForMetric}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamMetricsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as MetricSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForMetricWithoutPersonalSpace}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={FlowDownstreamMetricsWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{
            flowVizportalUrlId: props.flowVizportalUrlId,
            orderBy: props.orderBy as MetricSortOrder,
          }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForMetricWithoutPersonalSpace}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  );
}
